/**
 * ============================================================================
 * TRANSACTION SERVICE - API CLIENT
 * ============================================================================
 * 
 * Handles all transaction-related API calls
 */

import api, { handleApiError } from './api';

/**
 * Create a new transaction
 */
export const createTransaction = async (data) => {
  try {
    const response = await api.post('/transactions', data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get transaction by ID
 */
export const getTransaction = async (transactionId) => {
  try {
    const response = await api.get(`/transactions/${transactionId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get public transaction by short link
 */
export const getPublicTransaction = async (shortLink) => {
  try {
    const response = await api.get(`/transactions/public/${shortLink}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get public transaction by ID (no auth) – for payment page when not logged in or token missing
 */
export const getPublicTransactionById = async (transactionId) => {
  try {
    const response = await api.get(`/transactions/public/by-id/${transactionId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * List transactions
 */
export const listTransactions = async (filters = {}) => {
  try {
    const response = await api.get('/transactions', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Update transaction
 */
export const updateTransaction = async (transactionId, data) => {
  try {
    const response = await api.patch(`/transactions/${transactionId}`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Cancel transaction
 */
export const cancelTransaction = async (transactionId, reason) => {
  try {
    const response = await api.post(`/transactions/${transactionId}/cancel`, {
      reason,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Mark transaction as delivered
 */
export const markAsDelivered = async (transactionId, data) => {
  try {
    const response = await api.post(`/transactions/${transactionId}/deliver`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Approve delivery
 */
export const approveDelivery = async (transactionId) => {
  try {
    const response = await api.post(`/transactions/${transactionId}/approve`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Reject delivery
 */
export const rejectDelivery = async (transactionId, reason) => {
  try {
    const response = await api.post(`/transactions/${transactionId}/reject`, {
      reason,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get transaction statistics
 */
export const getTransactionStats = async () => {
  try {
    const response = await api.get('/transactions/stats');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get transaction summary
 */
export const getTransactionSummary = async () => {
  try {
    const response = await api.get('/transactions/summary');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Search transactions
 */
export const searchTransactions = async (query, page = 1, limit = 20) => {
  try {
    const response = await api.get('/transactions/search', {
      params: { q: query, page, limit },
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get transaction by reference ID
 */
export const getTransactionByReference = async (referenceId) => {
  try {
    const response = await api.get(`/transactions/ref/${referenceId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Send transaction link
 */
export const sendTransactionLink = async (transactionId, data) => {
  try {
    const response = await api.post(`/transactions/${transactionId}/send-link`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Join transaction (as counterparty)
 */
export const joinTransaction = async (transactionId, data) => {
  try {
    const response = await api.post(`/transactions/${transactionId}/join`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Accept transaction
 */
export const acceptTransaction = async (transactionId) => {
  try {
    const response = await api.post(`/transactions/${transactionId}/accept`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Request revision
 */
export const requestRevision = async (transactionId, data) => {
  try {
    const response = await api.post(`/transactions/${transactionId}/request-revision`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get transaction timeline
 */
export const getTransactionTimeline = async (transactionId) => {
  try {
    const response = await api.get(`/transactions/${transactionId}/timeline`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get transaction status history
 */
export const getTransactionStatusHistory = async (transactionId) => {
  try {
    const response = await api.get(`/transactions/${transactionId}/status-history`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export default {
  createTransaction,
  getTransaction,
  getPublicTransaction,
  listTransactions,
  updateTransaction,
  cancelTransaction,
  markAsDelivered,
  approveDelivery,
  rejectDelivery,
  getTransactionStats,
  getTransactionSummary,
  searchTransactions,
  getTransactionByReference,
  sendTransactionLink,
  joinTransaction,
  acceptTransaction,
  requestRevision,
  getTransactionTimeline,
  getTransactionStatusHistory,
};
